import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

const GOOGLE_CLIENT_ID = '581536844961-s7kfoqcp0ekr8ja651shaihtj01qr5dc.apps.googleusercontent.com';

const Login = () => {
  const { isAuthenticated, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const sdkReady = useRef(false);

  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Завантажуємо SDK при монтуванні, щоб при кліку не було await (Safari блокує попапи після async)
  useEffect(() => {
    if (window.google?.accounts?.oauth2) {
      sdkReady.current = true;
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => { sdkReady.current = true; };
    document.head.appendChild(script);
  }, []);

  const handleGoogleLogin = () => {
    if (loading) return;

    if (!sdkReady.current) {
      toast.error('Зачекайте, сторінка ще завантажується');
      return;
    }

    setLoading(true);

    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'openid email profile',
      ux_mode: 'popup',
      callback: async (response) => {
        if (response.error) {
          toast.error('Авторизацію скасовано');
          setLoading(false);
          return;
        }

        try {
          const authResult = await loginWithGoogle({
            code: response.code,
            redirectUri: 'postmessage',
          });

          if (authResult.success) {
            toast.success('Авторизація успішна!');
            navigate(from, { replace: true });
          } else {
            toast.error(authResult.error || 'Помилка авторизації');
          }
        } catch (error) {
          toast.error(`Помилка при авторизації: ${error.message}`);
        } finally {
          setLoading(false);
        }
      },
    });

    client.requestCode();
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4 py-6 sm:py-0 relative overflow-hidden bg-primary-900">
      {/* Фонові декоративні елементи */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-800 via-primary-900 to-blue-950" />
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-500 rounded-full opacity-10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-blue-400 rounded-full opacity-10 blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-primary-400 rounded-full opacity-5 blur-2xl" />
      </div>

      {/* Картка */}
      <div className="max-w-lg w-full relative z-10">
        <div className="bg-white/95 rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-6 sm:p-10">
            {/* Логотип і заголовок */}
            <div className="text-center mb-6 sm:mb-8">
              <img
                src="/logo.png"
                alt="ПрофБонус"
                className="h-16 w-16 sm:h-20 sm:w-20 mx-auto mb-4 object-contain"
                width="80"
                height="80"
              />
              <h1 className="text-2xl sm:text-3xl font-bold text-primary-800">
                Профспілковий бонус
              </h1>
              <p className="text-sm sm:text-base text-gray-500 mt-2 sm:mt-3">
                Програма лояльності для співробітників Чернівецького національного університету імені Юрія Федьковича
              </p>
            </div>

            {/* Інфо-блок */}
            <div className="border-l-4 border-primary-500 pl-3 sm:pl-4 py-2 bg-primary-50 mb-4 sm:mb-6 rounded-r">
              <p className="text-sm sm:text-base text-gray-700">
                Увійдіть за допомогою вашого облікового запису Google, пов'язаного з корпоративною поштою університету або авторизованою адресою.
              </p>
            </div>

            {/* Кнопка входу */}
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-md active:scale-[0.98] transition-all duration-150 group disabled:opacity-60 disabled:pointer-events-none"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-primary-600" />
                  <span className="text-gray-500 font-semibold text-base">Завантаження...</span>
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  <span className="text-gray-700 font-semibold text-base group-hover:text-gray-900">
                    Увійти через Google
                  </span>
                </>
              )}
            </button>
          </div>

          {/* Футер */}
          <div className="px-6 sm:px-10 py-4 bg-gray-50/80 border-t border-gray-100">
            <p className="text-center text-xs text-gray-400">
              Використовуючи цю систему, ви погоджуєтесь з{' '}
              <a href="#" className="text-primary-500 hover:text-primary-700">умовами використання</a> та{' '}
              <a href="#" className="text-primary-500 hover:text-primary-700">політикою конфіденційності</a>
            </p>
            <p className="text-center text-xs text-gray-300 mt-2">
              &copy; {new Date().getFullYear()} Профспілковий комітет ЧНУ ім. Ю.Федьковича
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
